import { useMemo, useState } from 'react'
import { FileText, BookOpen, ListChecks, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useNotes } from '@/hooks/useNotes'
import { useJournal } from '@/hooks/useJournal'
import { useLists } from '@/hooks/useLists'
import { formatDate } from '@/lib/utils'
import {
  LINK_FIELD_BY_TARGET,
  canLink,
  linksOf as linksOfHolder,
  isLinkedTo,
  withLink,
  type LinkTargetType,
} from '@trackmind/core'

/**
 * "Attached" — the notes, journal entries, and lists connected to a goal,
 * project, habit, or task, with attach/detach controls ON THIS SIDE.
 *
 * The link arrays live on the attachment objects (note.linked_goal_ids etc.),
 * and until now they could only be edited from the note/journal/list editors.
 * That made the connection one-directional in practice: standing on a goal you
 * could see attachments but not add one. This component writes to the OTHER
 * object's array, so the same link can be managed from either end, at any
 * time — nothing is fixed at creation.
 *
 * The picker is inline-expandable rather than a nested dialog, so it works
 * identically inside the goal bottom-sheet, the task detail dialog, and plain
 * page surfaces — and on phones it's just stacked content with ~44px rows.
 * Attach/detach persist immediately; there is no save step to forget.
 */

/**
 * Re-exported rather than redeclared: the field map and the attach/detach
 * rules now live in core, so this component and the MCP connector attach the
 * same way. They did not — the arrays were only reachable from here, which is
 * why the assistant could not attach anything at all.
 */
export type AttachTargetType = LinkTargetType

export function AttachedItemsSection({
  targetType,
  targetId,
  className,
}: {
  targetType: AttachTargetType
  targetId: string
  className?: string
}) {
  const { notes, updateNote } = useNotes()
  const { entries, updateEntry } = useJournal()
  const { lists, updateList } = useLists()

  const [picking, setPicking] = useState(false)
  const [search, setSearch] = useState('')

  const field = LINK_FIELD_BY_TARGET[targetType]
  // TaskList has no linked_task_ids — lists can attach to goals/projects/habits only
  const listsSupported = canLink('list', targetType)

  const linksOf = (obj: object): string[] => linksOfHolder(obj, field)
  const isLinked = (obj: object) => isLinkedTo(obj, field, targetId)

  const attached = useMemo(() => ([
    ...notes.filter(isLinked).map(n => ({
      kind: 'note' as const, id: n.id, label: n.title, icon: FileText, current: linksOf(n),
    })),
    ...entries.filter(isLinked).map(e => ({
      kind: 'journal' as const, id: e.id, label: e.title || formatDate(e.date), icon: BookOpen, current: linksOf(e),
    })),
    ...(listsSupported ? lists.filter(l => !l.archived && isLinked(l)).map(l => ({
      kind: 'list' as const, id: l.id, label: l.name, icon: ListChecks, current: linksOf(l),
    })) : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [notes, entries, lists, targetId, field, listsSupported])

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase()
    const match = (label: string) => !q || label.toLowerCase().includes(q)
    return [
      ...notes.filter(n => !isLinked(n) && match(n.title)).map(n => ({
        kind: 'note' as const, id: n.id, label: n.title, icon: FileText, current: linksOf(n),
      })),
      ...entries.filter(e => !isLinked(e) && match(e.title || e.date)).map(e => ({
        kind: 'journal' as const, id: e.id, label: e.title || formatDate(e.date), icon: BookOpen, current: linksOf(e),
      })),
      ...(listsSupported ? lists.filter(l => !l.archived && !isLinked(l) && match(l.name)).map(l => ({
        kind: 'list' as const, id: l.id, label: l.name, icon: ListChecks, current: linksOf(l),
      })) : []),
    ].slice(0, 12)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, entries, lists, targetId, field, listsSupported, search])

  const persist = async (kind: 'note' | 'journal' | 'list', id: string, next: string[]) => {
    // The computed key is one of the four linked_*_ids fields, all of which
    // exist on the note/journal update types (lists lack linked_task_ids, but
    // the list path is unreachable for targetType 'task').
    const patch = { [field]: next } as Record<typeof field, string[]>
    if (kind === 'note') await updateNote(id, patch)
    else if (kind === 'journal') await updateEntry(id, patch)
    else await updateList(id, patch)
  }

  const attach = (kind: 'note' | 'journal' | 'list', id: string, current: string[]) =>
    persist(kind, id, withLink(current, targetId, true))
  const detach = (kind: 'note' | 'journal' | 'list', id: string, current: string[]) =>
    persist(kind, id, withLink(current, targetId, false))

  return (
    <div className={className ?? 'space-y-2 border-t pt-3'}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Attached</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => { setPicking(!picking); setSearch('') }}
        >
          {picking ? <><X className="h-3.5 w-3.5 mr-1" />Done</> : <><Plus className="h-3.5 w-3.5 mr-1" />Attach</>}
        </Button>
      </div>

      {attached.length === 0 && !picking && (
        <p className="text-xs text-muted-foreground">
          Nothing attached. Connect notes, journal entries{listsSupported ? ', or lists' : ''} —
          links can be added or removed at any time, from either side.
        </p>
      )}

      {attached.length > 0 && (
        <div className="space-y-1">
          {attached.map(a => (
            <div key={`${a.kind}-${a.id}`} className="flex items-center gap-2 p-2 rounded bg-muted/30 text-sm">
              <a.icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0 truncate">{a.label}</span>
              <span className="text-xs text-muted-foreground shrink-0">{a.kind}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                aria-label={`Detach ${a.label}`}
                onClick={() => detach(a.kind, a.id, a.current)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {picking && (
        <div className="space-y-1.5">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search notes, journal${listsSupported ? ', lists' : ''}…`}
              className="h-8 pl-7 text-xs"
            />
          </div>
          <div className="max-h-44 overflow-y-auto rounded-md border divide-y">
            {candidates.length === 0 ? (
              <p className="p-2 text-xs text-muted-foreground">Nothing to attach</p>
            ) : (
              candidates.map(c => (
                <button
                  key={`${c.kind}-${c.id}`}
                  type="button"
                  className="flex w-full items-center gap-2 px-2 py-2 text-left text-sm hover:bg-muted/50"
                  onClick={() => attach(c.kind, c.id, c.current)}
                >
                  <c.icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0 truncate">{c.label}</span>
                  <Badge variant="secondary" className="text-[10px] shrink-0">{c.kind}</Badge>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
